import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  api,
  ApiError,
  CATEGORIES,
  FUEL_UNITS,
  LOAD_UNITS,
  STATUSES,
  type Aircraft,
  type AircraftInput,
} from "../lib/api";
import { Alert, Button, Field, Input, Select, Textarea } from "./ui";

const CATEGORY_LABEL: Record<string, string> = {
  helicopter: "Helicopter",
  fixed_wing: "Fixed wing",
};

const STATUS_LABEL: Record<string, string> = {
  serviceable: "Serviceable",
  unserviceable: "Unserviceable",
  retired: "Retired",
};

/** What the load actually is, so the field means something in context. */
function loadHint(category: string): string {
  return category === "helicopter"
    ? "What it carries per drop or lift. Lets reports show litres, not just a count."
    : "What it carries per load. Lets reports show litres, not just a count.";
}

const EMPTY: AircraftInput = {
  registration: "",
  callsign: "",
  model: "",
  category: "helicopter",
  fuelUnit: "litres",
  fuelCapacity: "",
  loadCapacity: "",
  status: "serviceable",
  notes: "",
};

function toInput(a: Aircraft): AircraftInput {
  return {
    registration: a.registration,
    callsign: a.callsign ?? "",
    model: a.model ?? "",
    category: a.category,
    fuelUnit: a.fuelUnit,
    fuelCapacity: a.fuelCapacity ?? "",
    loadCapacity: a.loadCapacity ?? "",
    ...(a.loadUnit ? { loadUnit: a.loadUnit } : {}),
    status: a.status,
    notes: a.notes ?? "",
  };
}

export function AircraftDialog({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  editing: Aircraft | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  // The server refuses a rename once the aircraft has flown; saying so up front beats a failed save.
  const locked = Boolean(editing && (editing.missionCount ?? 0) > 0);
  const [form, setForm] = useState<AircraftInput>(EMPTY);
  const [error, setError] = useState<ApiError | string | null>(null);
  const [busy, setBusy] = useState(false);

  // A native <dialog> gives focus trapping and Escape handling for free rather
  // than reimplementing both badly.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    if (open) {
      setForm(editing ? toInput(editing) : EMPTY);
      setError(null);
    }
  }, [open, editing]);

  const set =
    <K extends keyof AircraftInput>(key: K) =>
    (e: { target: { value: string } }) =>
      setForm((f) => ({ ...f, [key]: e.target.value as AircraftInput[K] }));

  const fieldError = (f: string) => (error instanceof ApiError ? error.fieldError(f) : undefined);
  const topError =
    error instanceof ApiError ? (error.issues.length === 0 ? error.message : null) : error;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);

    // Empty optional strings are dropped rather than sent as "", so a blank
    // field reads as "not provided" instead of failing numeric validation.
    const payload: AircraftInput = { ...form };
    for (const key of ["callsign", "model", "fuelCapacity", "loadCapacity", "notes"] as const) {
      if (!payload[key]) delete payload[key];
    }
    if (!payload.loadCapacity) delete payload.loadUnit;
    else payload.loadUnit ??= "litres";

    try {
      if (editing) await api.updateAircraft(editing.id, payload);
      else await api.createAircraft(payload);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err : "Couldn't save this aircraft.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog
      ref={ref}
      onCancel={onClose}
      onClick={(e) => e.target === ref.current && onClose()}
      className="bg-transparent p-0 backdrop:bg-black/70 m-auto max-h-[92vh]"
    >
      {/* Fixed header and footer with only the fields scrolling — otherwise a
          form this tall pushes its own submit button off a laptop screen. */}
      <div className="w-[min(38rem,92vw)] max-h-[92vh] flex flex-col bg-panel border border-rule-soft rounded-2xl shadow-2xl">
        {/* Laid out like an iOS sheet: Cancel on the left, the title centred,
            and the confirming action on the right. */}
        <div className="shrink-0 px-4 h-14 border-b border-rule-soft grid grid-cols-[1fr_auto_1fr] items-center">
          <button
            type="button"
            onClick={onClose}
            className="justify-self-start text-[1rem] text-accent-text hover:underline underline-offset-4"
          >
            Cancel
          </button>
          <h2 className="text-[1.05rem] font-semibold text-ink">
            {editing ? editing.registration : "New aircraft"}
          </h2>
          <span />
        </div>

        <form onSubmit={onSubmit} className="flex flex-col min-h-0 flex-1" noValidate>
          <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-5">
          {topError && <Alert>{topError}</Alert>}

          <div className="grid sm:grid-cols-2 gap-5">
            <Field
              label="Registration"
              hint={locked ? "Locked: this aircraft has missions." : "Painted on the airframe."}
              error={fieldError("registration")}
            >
              {(id) => (
                <Input
                  id={id}
                  required
                  autoFocus={!locked}
                  readOnly={locked}
                  value={form.registration}
                  onChange={set("registration")}
                  invalid={!!fieldError("registration")}
                  placeholder="VH-SRN"
                  className="font-mono uppercase"
                />
              )}
            </Field>

            <Field label="Callsign" hint="Can change each season." error={fieldError("callsign")}>
              {(id) => (
                <Input id={id} value={form.callsign ?? ""} onChange={set("callsign")} placeholder="Helitak 350" />
              )}
            </Field>

            <Field label="Type / model" error={fieldError("model")}>
              {(id) => (
                <Input id={id} value={form.model ?? ""} onChange={set("model")} placeholder="Bell 412EP" />
              )}
            </Field>

            <Field label="Category">
              {(id) => (
                <Select id={id} value={form.category} onChange={set("category")}>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          <div className="border-t border-rule-soft pt-5 grid sm:grid-cols-2 gap-5">
            <Field label="Fuel unit">
              {(id) => (
                <Select id={id} value={form.fuelUnit} onChange={set("fuelUnit")}>
                  {FUEL_UNITS.map((u) => (
                    <option key={u} value={u}>{u[0]!.toUpperCase() + u.slice(1)}</option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label="Fuel capacity" hint="Optional." error={fieldError("fuelCapacity")}>
              {(id) => (
                <Input
                  id={id}
                  inputMode="decimal"
                  value={form.fuelCapacity ?? ""}
                  onChange={set("fuelCapacity")}
                  invalid={!!fieldError("fuelCapacity")}
                  placeholder="1200"
                  className="font-mono"
                />
              )}
            </Field>

            <Field label="Load capacity" hint={loadHint(form.category)} error={fieldError("loadCapacity")}>
              {(id) => (
                <Input
                  id={id}
                  inputMode="decimal"
                  value={form.loadCapacity ?? ""}
                  onChange={set("loadCapacity")}
                  invalid={!!fieldError("loadCapacity")}
                  placeholder="1200"
                  className="font-mono"
                />
              )}
            </Field>

            <Field label="Load unit">
              {(id) => (
                <Select
                  id={id}
                  value={form.loadUnit ?? "litres"}
                  onChange={set("loadUnit")}
                  disabled={!form.loadCapacity}
                >
                  {LOAD_UNITS.map((u) => (
                    <option key={u} value={u}>{u[0]!.toUpperCase() + u.slice(1)}</option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          <div className="border-t border-rule-soft pt-5 flex flex-col gap-5">
            <Field label="Status">
              {(id) => (
                <Select id={id} value={form.status} onChange={set("status")}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label="Notes">
              {(id) => (
                <Textarea
                  id={id}
                  value={form.notes ?? ""}
                  onChange={set("notes")}
                  placeholder="Anything the crew should know."
                />
              )}
            </Field>
          </div>

          </div>

          <div className="shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-rule-soft bg-panel rounded-b-2xl">
            <Button type="submit" busy={busy} className="w-full sm:w-auto">
              {editing ? "Save changes" : "Add aircraft"}
            </Button>
          </div>
        </form>
      </div>
    </dialog>
  );
}
